import React, { useState } from 'react';
import { CommandBarButton, IconButton, Dialog, DialogFooter, DialogType, PrimaryButton, DefaultButton, TextField } from '@fluentui/react';
import DetailsTable from '../../../../Table/DetailsTable';
import FeatureSummary from '../../../../Util/FeatureSummary';

const ApiKeysTable = ({
    apiKeys,
    setSelectedApiKey,
    loading,
    editOnClick,
    createOnClick,
    deleteOnClick,
}) => {
    const columns = [
        {
            minWidth: 30,
            maxWidth: 30,
            onRender: (item) => (
                <ContextualMenu editOnClick={editOnClick} id={item.keySid} item={item} deleteOnClick={deleteOnClick} />
            ),
        },
        {
            name: 'Client Name',
            fieldName: 'clientName',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            isSorted: true,
            isSortedDescending: false,
        },
        {
            name: 'Key Name',
            fieldName: 'keyName',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
        },
        {
            name: 'Scopes',
            minWidth: 200,
            maxWidth: 300,
            isResizable: true,
            onRender: (item) => {
                const scopeNames = item.scopes?.map(s => `${s.scopeGroup}.${s.scope}`).join(', ') || 'None';
                return <span>{scopeNames}</span>;
            },
        },
    ];

    const items = apiKeys ?? [];

    const selectionHook = (items) => {
        const selectedItem = items && items[0];
        setSelectedApiKey(selectedItem && selectedItem.keySid);
    };

    return (
        <DetailsTable
            items={items}
            columns={columns}
            selectionMode="none"
            selectionHook={selectionHook}
            loading={loading}
            mainColumn={'keyName'}
            style={{
                position: 'relative',
                height: '80vh',
                width: '80vw',
            }}
            CommandBar={
                <>
                    <CommandBarButton
                        iconProps={{ iconName: 'Add' }}
                        text="Add API Key"
                        onClick={() => createOnClick()}
                    />
                    <FeatureSummary
                        label="About Api Keys"
                        summary="This table displays all API keys for each client, including the client name, key name, and scopes. You can add, edit, or delete API keys. API keys are used to authenticate and authorize access to the platform's APIs."                    
                    />
                </>
            }
        />
    );
};

const moreIcon = { iconName: 'More' };

const ContextualMenu = ({ editOnClick, deleteOnClick, id, item, ...rest }) => {
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [confirmText, setConfirmText] = useState('');

    const handleDeleteClick = () => {
        setShowDeleteDialog(true);
    };

    const handleConfirmDelete = () => {
        if (confirmText === item.keyName) {
            deleteOnClick(id);
            setShowDeleteDialog(false);
            setConfirmText('');
        }
    };

    const menuProps = {
        items: [
            {
                key: 'edit',
                iconProps: { iconName: 'Edit' },
                text: 'Edit',
                onClick: () => editOnClick(id),
            },
            {
                key: "delete",
                iconProps: { iconName: "Delete" },
                text: "Delete",
                onClick: handleDeleteClick,
            },
        ],
    };

    return (
        <>
            <IconButton iconProps={moreIcon} menuProps={menuProps} onRenderMenuIcon={() => {}} {...rest} />
            <Dialog
                hidden={!showDeleteDialog}
                onDismiss={() => {
                    setShowDeleteDialog(false);
                    setConfirmText('');
                }}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Delete API Key',
                    subText: `Are you sure you want to delete this API key? This action cannot be undone. Type "${item.keyName}" to confirm.`,
                }}
            >
                <TextField
                    value={confirmText}
                    onChange={(e, newValue) => setConfirmText(newValue)}
                    placeholder="Type key name to confirm"
                />
                <DialogFooter>
                    <PrimaryButton
                        onClick={handleConfirmDelete}
                        text="Delete"
                        disabled={confirmText !== item.keyName}
                    />
                    <DefaultButton
                        onClick={() => {
                            setShowDeleteDialog(false);
                            setConfirmText('');
                        }}
                        text="Cancel"
                    />
                </DialogFooter>
            </Dialog>
        </>
    );
};

export default ApiKeysTable;
